import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { Exclude, Expose } from "class-transformer";
import { IsEnum, IsNumber, IsOptional } from "class-validator";
import { Subject } from "rxjs";
import { v4 as uuidv4 } from "uuid";

export enum DustExZoneType {
  ZONE_20 = "ZONE_20",
  ZONE_21 = "ZONE_21",
  ZONE_22 = "ZONE_22",
}

export class DustExZone extends FloorplanItem {
  @IsNumber()
  @Expose({ name: "width" })
  private _width: number;

  @IsNumber()
  @Expose({ name: "height" })
  private _height: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "referenceWidth" })
  private _referenceWidth?: number;

  @IsNumber()
  @IsOptional()
  @Expose({ name: "_referenceHeight" })
  private _referenceHeight?: number;

  @IsNumber()
  @Expose({ name: "rotation" })
  private _rotation: number;

  @IsEnum(DustExZoneType)
  public dustExZoneType: DustExZoneType;

  @Exclude()
  public sizeChanged: Subject<{ width: number; height: number }>;

  @Exclude()
  referenceSizeChanged: Subject<{ referenceWidth: number | undefined; referenceHeight: number | undefined }>;

  constructor(
    floorplan: Floorplan,
    id: string,
    exZoneType: DustExZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
  ) {
    super(floorplan, id, x, y);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.dustExZoneType = exZoneType;
    this.sizeChanged = new Subject();
    this.referenceSizeChanged = new Subject();
    this.updateReferenceSize(this._width, this._height);
  }

  get width(): number {
    return this._width;
  }

  get height(): number {
    return this._height;
  }

  get referenceWidth(): number | undefined {
    return this._referenceWidth;
  }

  get referenceHeight(): number | undefined {
    return this._referenceHeight;
  }

  set referenceWidth(referenceWidth: number | undefined) {
    this._referenceWidth = referenceWidth;
  }

  set referenceHeight(referenceHeight: number | undefined) {
    this._referenceHeight = referenceHeight;
  }

  get rotation(): number {
    return this._rotation;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const dustExZoneType = this.dustExZoneType;
    const height = this.height;
    const rotation = this.rotation;
    const width = this.width;

    return (x: number, y: number) => DustExZone.create(this._floorplan, dustExZoneType, x, y, width, height, rotation);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.dustExZones;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as DustExZone,
        this._floorplan.dustExZones,
        FloorplanEventType.DUST_EX_ZONE_ADDED,
        () => this._floorplan.project.setDustExZonePositionIds(),
        afterDuplicateAdded as ((duplicate: DustExZone) => void) | undefined,
      );
  }

  override delete() {
    this.sizeChanged.unsubscribe();
    this._floorplan.deleteDustExZone(this);
  }

  updateReferenceSize(width: number, height: number) {
    if (this.isReferenceMeasureSet() && width && height) {
      this._referenceWidth = Math.round((width / this._floorplan.getReferenceScale()) * 100) / 100;
      this._referenceHeight = Math.round((height / this._floorplan.getReferenceScale()) * 100) / 100;
    } else {
      this._referenceWidth = undefined;
      this._referenceHeight = undefined;
    }
  }

  updateSize(referenceWidth: number, referenceHeight: number) {
    this._width = referenceWidth * this._floorplan.getReferenceScale();
    this._height = referenceHeight * this._floorplan.getReferenceScale();
    this.updateForm(this._width, this._height, this._rotation, this.x, this.y);
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED_BY_INPUT, this);
  }

  updateForm(width: number, height: number, rotation: number, x: number, y: number) {
    this.sizeChanged.next({ width, height });
    this.updateReferenceSize(width, height);
    this._width = width;
    this._height = height;
    this._rotation = rotation;
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.dustExZonesItemTextVisible;
  }

  isReferenceMeasureSet() {
    if (this._floorplan) return !!this._floorplan.referenceMeasure;
    return false;
  }

  get relatedItems(): FloorplanItem[] {
    return this._floorplan.dustExZones;
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshDustExZonesLock();
  }

  static create(
    floorplan: Floorplan,
    dustExZoneType: DustExZoneType,
    x: number,
    y: number,
    width: number,
    height: number,
    rotation: number,
  ) {
    return new DustExZone(floorplan, uuidv4(), dustExZoneType, x, y, width, height, rotation);
  }
}
